import React, { useState, useMemo, useEffect } from 'react';
import { toast } from "react-toastify";
import LoadingOverlay from '../loading/loading';
import service from '../../services/service';

const HotelsList = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 4;

    useEffect(() => {
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        try {
            setLoading(true);
            const response = await service.save({ userId: sessionStorage.getItem("userId") }, `bookings/`, "POST");
            if (response && !response.success) {
                toast.error(response.message);
                return;
            }
            setData(response.data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }

    // Define visible columns and their labels
    const columns = [
        { key: 'hotelName', label: 'Hotel ', sortable: true },
        { key: 'commissionRate', label: 'Commission ', sortable: true },
        // { key: 'pricePerNight', label: 'Price/Night ', sortable: true },
        // { key: 'dateFrom', label: 'Check-In ', sortable: true },
        // { key: 'dateTo', label: 'Check-Out ', sortable: true },
        // { key: 'hotelAddress', label: 'hotelAddress ', sortable: true },
        { key: 'userName', label: 'Booked By ', sortable: true },
        { key: 'totalAmount', label: 'Amount ', sortable: true },
        // { key: 'status', label: 'Status ', sortable: true },
    ];

    const styles = {
        saveButton: {
            padding: '5px 10px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
        },
        container: {
            width: '100%',
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '24px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
        },
        title: {
            fontSize: '24px',
            fontWeight: '600',
            color: '#111827',
            margin: '0',
        },
        searchContainer: {
            position: 'relative',
            width: '320px',
            display: 'flex',
            alignItems: 'center',
        },
        searchInput: {
            width: '100%',
            padding: '10px 16px 10px 40px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            fontSize: '14px',
            outline: 'none',
            transition: 'all 0.2s',
            marginRight: '10px',
        },
        tableContainer: {
            overflowX: 'auto',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px',
            textAlign: 'left',
        },
        th: {
            padding: '14px 20px',
            backgroundColor: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            color: '#374151',
            fontWeight: '600',
            cursor: 'pointer',
            userSelect: 'none',
            whiteSpace: 'nowrap',
        },
        td: {
            padding: '14px 20px',
            borderBottom: '1px solid #e5e7eb',
            color: '#374151',
        },
        badge: {
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
        },
        actionButton: {
            padding: '6px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            borderRadius: '4px',
            color: '#6b7280',
            transition: 'all 0.2s',
        },
        pagination: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '20px',
            padding: '10px 0',
        },
        pageButton: {
            padding: '8px 16px',
            margin: '0 4px',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#374151',
            transition: 'all 0.2s',
        },
        icon: {
            fontSize: '16px',
            display: 'inline-block',
            cursor: 'pointer',
            userSelect: 'none',
        },
        searchIcon: {
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#6b7280',
            fontSize: '14px',
        },
        actionIcon: {
            padding: '6px 8px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            borderRadius: '4px',
            color: '#6b7280',
            transition: 'all 0.2s',
            fontSize: '16px',
            lineHeight: 1,
        },
        serialNo: {
            width: '70px',
            textAlign: 'center',
            color: '#6b7280',
            fontWeight: '500',
        }
    };

    const handleActionButton = (type, id) => {
        try {
            if (type === 'cancel' || type === 'active') {
                const isConfirmed = window.confirm("Are you sure you want to " + type + " the booking?");
                if (isConfirmed) configueBooking(type, id);
            }
        } catch (error) {
            toast.error(error.message);
        }
    }

    const configueBooking = async (type, id) => {
        try {
            setLoading(true);
            const response = await service.view(`bookings/${type}/${id}`, "GET");
            if (!response.success) {
                toast.error(`${response.message} ${response.displayMessage}`);
                return;
            }
            toast.success(`${response.message} ${response.displayMessage}`);
            fetchBookings();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const cancelBooking = async (bookingId) => {
        const isConfirmed = window.confirm("Are you sure you want to cancel and deduct the amount for this booking?");
        if (isConfirmed) {
            try {
                setLoading(true);
                const response = await service.save({ bookingId }, `bookings/cancel-booking`, "POST");
                if (!response.success) {
                    toast.error(`${response.error}`);
                    return;
                }
                toast.success(`${response.message}`);
                fetchBookings();
            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoading(false);
            }
        }
    }

    const getBooleanBadgeStyle = (value) => ({
        ...styles.badge,
        backgroundColor: value ? '#def7ec' : '#fee2e2',
        color: value ? '#03543f' : '#991b1b',
    });

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    // Icons using Unicode symbols
    const icons = {
        search: '🔍',
        edit: '✏️',
        delete: '🗑️',
        block: '🚫',
        unblock: '🔓',
        more: '⋮',
        check: '✓',
        cross: '✕',
        sortUp: '▲',
        sortDown: '▼',
        sortBoth: '⇵',
        checkedOut: '✅',
        checkedOutP: '🚶‍♂️',
        cancelBooking: '📛',
    };

    const sortedAndFilteredData = useMemo(() => {
        let filteredData = [...data];

        if (searchTerm) {
            filteredData = filteredData.filter(item => {
                const searchFields = [
                    item.hotelName,
                    item.hotelAddress,
                    item.userName,
                    item.status,
                    item.pricePerNight,
                    item.commissionRate,
                ];
                return searchFields.some(field =>
                    field?.toString().toLowerCase().includes(searchTerm.toLowerCase())
                );
            });
        }

        if (sortConfig.key && sortConfig.key !== 'serialNo') {
            filteredData.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key === 'address') {
                    aValue = a.address;
                    bValue = b.address;
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        return filteredData;
    }, [data, searchTerm, sortConfig]);

    const paginatedData = sortedAndFilteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const pageCount = Math.ceil(sortedAndFilteredData.length / itemsPerPage);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const renderCellContent = (column, item, index) => {
        switch (column.key) {
            case 'serialNo':
                return (
                    <span style={{ color: '#2C3E50' }}>
                        {((currentPage - 1) * itemsPerPage) + index + 1}
                    </span>
                );
            case 'status':
                return (
                    <span
                        style={{
                            ...getBooleanBadgeStyle(item[column.key]),
                            color: item[column.key] ? '#27AE60' : '#E74C3C',
                            fontWeight: 'bold'
                        }}
                    >
                        {item[column.key] ? icons.check : icons.checkedOutP}
                        {' '}{item[column.key] ? 'Active' : 'Left'}
                    </span>
                );
            case 'dateFrom':
                return (
                    <span style={{ color: '#2980B9' }}>
                        {formatDate(item.dateFrom)}
                    </span>
                );
            case 'dateTo':
                return (
                    <span style={{ color: '#2980B9' }}>
                        {formatDate(item.dateTo)}
                    </span>
                );
            case 'totalAmount':
                return (
                    <span style={{ color: '#109d01' }}>
                        ₹ {Math.ceil(Math.abs(item.totalAmount)).toFixed(2)}
                    </span>
                );
            case 'pricePerNight':
                return (
                    <span style={{ color: '#F39C12' }}>
                        ₹ {Math.ceil(Math.abs(item.pricePerNight))}
                    </span>
                );
            case 'commissionRate':
                return (
                    <span style={{ color: '#8E44AD' }}>
                        {Math.ceil(Math.abs(item.commissionRate))} %
                    </span>
                );
            default:
                return (
                    <span style={{ color: '#021bff' }}>
                        {item[column.key] || '-'}
                    </span>
                );
        }
    };

    return (
        <>
            {loading && <LoadingOverlay />}
            <div style={styles.container}>
                <div style={styles.header}>
                    <h2 style={styles.title}>Bookings</h2>
                    <div style={styles.searchContainer}>
                        <span style={styles.searchIcon}>{icons.search}</span>
                        <input
                            type="text"
                            placeholder="Search Bookings..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={styles.searchInput}
                        />
                    </div>
                </div>
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        onClick={() => requestSort(column.key, column.sortable)}
                                        style={{
                                            ...styles.th,
                                            ...(column.key === 'serialNo' && styles.serialNo),
                                            cursor: column.sortable ? 'pointer' : 'default'
                                        }}
                                    >
                                        {column.label}
                                        {column.sortable && (
                                            <span style={styles.icon}>{sortConfig.key === column.key ?
                                                (sortConfig.direction === 'ascending' ? icons.sortUp : icons.sortDown)
                                                : icons.sortBoth}</span>
                                        )}
                                    </th>
                                ))}
                                {sessionStorage.getItem("isAdmin") === "true" && (<> <th style={styles.th}>Actions</th> </>)}
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.map((item) => (
                                <tr
                                    key={item.bookingId}
                                    style={{
                                        backgroundColor: 'white',
                                        transition: 'background-color 0.2s',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                >
                                    {columns.map((column) => (
                                        <td key={column.key} style={styles.td}>
                                            {renderCellContent(column, item)}
                                        </td>
                                    ))}
                                    {sessionStorage.getItem("isAdmin") === "true" && (<>
                                        <td style={styles.td}>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                {item.status ? (<><button
                                                    style={styles.actionIcon}
                                                    title="Mark Checkedout"
                                                    onClick={() => handleActionButton('cancel', item.bookingId)}
                                                >
                                                    {icons.checkedOut}
                                                </button>
                                                    <button style={styles.actionButton}
                                                        title='Cancel & Deduct Commission'
                                                        onClick={() => cancelBooking(item.bookingId)}>{icons.cancelBooking}</button></>
                                                ) : (<></>
                                                    //     <button
                                                    //     style={styles.actionIcon}
                                                    //     title="Activate Booking"
                                                    //     onClick={() => handleActionButton('active', item.bookingId)}
                                                    // >
                                                    //     {icons.unblock}
                                                    // </button>
                                                )}
                                            </div>
                                        </td>
                                    </>)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div style={styles.pagination}>
                    <span style={{ color: '#6b7280', fontSize: '14px' }}>
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                        {Math.min(currentPage * itemsPerPage, sortedAndFilteredData.length)} of{' '}
                        {sortedAndFilteredData.length} entries
                    </span>
                    <div>
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            style={{
                                ...styles.pageButton,
                                opacity: currentPage === 1 ? 0.5 : 1,
                            }}
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, pageCount))}
                            disabled={currentPage === pageCount}
                            style={{
                                ...styles.pageButton,
                                opacity: currentPage === pageCount ? 0.5 : 1,
                            }}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

export default HotelsList;